import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  USERNAME_MAX,
  USERNAME_MIN,
  adminRenameUsername,
  checkUsernameAvailable,
  validateUsernameFormat,
} from "@/lib/username";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetUserId: string;
  targetLabel: string;
  targetTier: string | null;
  onRenamed: () => void | Promise<void>;
};

/** Super-admin override dialog for renaming a user's @handle.
 *  Bypasses the cooldown + reservations block; hard collisions are still rejected server-side. */
export const AdminRenameUsernameDialog = ({
  open,
  onOpenChange,
  targetUserId,
  targetLabel,
  targetTier,
  onRenamed,
}: Props) => {
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const validation = useMemo(() => validateUsernameFormat(value), [value]);

  // Reset state whenever the dialog opens fresh
  useEffect(() => {
    if (!open) return;
    setValue("");
    setStatus("idle");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!validation.ok) {
      setStatus(value.length === 0 ? "idle" : "invalid");
      setError(value.length === 0 ? null : (validation as { ok: false; message: string }).message);
      return;
    }
    setStatus("checking");
    setError(null);
    const t = window.setTimeout(async () => {
      const ok = await checkUsernameAvailable(validation.normalized);
      setStatus(ok ? "available" : "taken");
      if (!ok) setError("Reserved or in use. Submitting will still be rejected if another account already owns it.");
    }, 350);
    return () => window.clearTimeout(t);
  }, [open, value, validation]);

  const submit = async () => {
    if (!validation.ok) return;
    setBusy(true);
    try {
      await adminRenameUsername(targetUserId, validation.normalized);
      toast.success(`Renamed to @${validation.normalized}`);
      onOpenChange(false);
      await onRenamed();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rename failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename username (super-admin override)</DialogTitle>
          <DialogDescription>
            Bypasses the 365-day cooldown and the reservations block. Hard collisions with
            another live account are still rejected. The previous handle, if any, will be
            parked in reservations for 90 days.
            {targetTier && (
              <span className="mt-2 block text-xs">
                Target: <span className="font-medium text-foreground">{targetLabel}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="admin-rename-username">New username</Label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground">@</span>
              <Input
                id="admin-rename-username"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={value}
                onChange={(e) => setValue(e.target.value.toLowerCase().slice(0, USERNAME_MAX))}
                className="pl-7 pr-9"
                placeholder="newhandle"
                disabled={busy}
              />
              <span className="absolute inset-y-0 right-3 flex items-center">
                {status === "checking" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : status === "available" ? (
                  <Check className="h-4 w-4 text-primary" />
                ) : status === "taken" || status === "invalid" ? (
                  <X className="h-4 w-4 text-destructive" />
                ) : null}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {USERNAME_MIN}–{USERNAME_MAX} characters. Lowercase letters, numbers, and underscores.
            </p>
            {error && (
              <p className={`text-sm ${status === "taken" ? "text-muted-foreground" : "text-destructive"}`}>
                {error}
              </p>
            )}
            {status === "available" && !error && (
              <p className="text-sm text-primary">
                @{validation.ok ? validation.normalized : ""} is available.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={busy || !validation.ok}>
              {busy ? "Renaming…" : "Force rename"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
